<template>
    <div class="account-geo-details" v-if="account">
        <div class="account-geo-header" v-if="geoInfo">
            <div class="account-geo-header-info">
                <div class="account-geo-field">
                    <div class="font-weight-medium mr-2">
                        Geo OK:
                    </div>
                    <div>
                        {{ geoInfo.geoOK ? "Yes" : "No" }}
                    </div>
                </div>
                <div class="account-geo-field">
                    <div class="font-weight-medium mr-2">
                        Bus Address 1:
                    </div>
                    <div>
                        {{ geoInfo.busAddress1 }}
                    </div>
                </div>
                <div class="account-geo-field" v-if="geoInfo.busAddress2">
                    <div class="font-weight-medium mr-2">
                        Bus Address 2:
                    </div>
                    <div>
                        {{ geoInfo.busAddress2 }}
                    </div>
                </div>
            </div>
            <div class="account-geo-header-phones">
                <div
                    class="account-geo-field"
                    v-for="(phone, phoneIndex) in phoneNumbers"
                    :key="phoneIndex"
                >
                    <div class="font-weight-medium mr-2">
                        {{ getPhoneType(phone.phoneType) }}:
                    </div>
                    <div>
                        {{ phone.phoneNum }}
                    </div>
                </div>
            </div>
        </div>

        <v-progress-linear
            v-if="loading"
            :indeterminate="true"
            color="success"
        ></v-progress-linear>

        <div v-if="!loading">
            <div class="account-geo-section">
                <div class="account-geo-section-title">
                    <v-divider></v-divider>
                    <div class="caption font-weight-medium grey--text text--darken-1 mx-3 mb-0">
                        GEO STUDENTS
                    </div>
                    <v-divider></v-divider>
                </div>

                <div
                    class="caption grey--text mt-2"
                    v-if="!geoStudents.length"
                >
                    No geo students
                </div>

                <table class="geo-students-table" v-else>
                    <thead>
                        <tr>
                            <th>Student</th>
                            <th>ID / Class</th>
                            <th>Address</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="student in geoStudents"
                            :key="geoStudentKey(student)"
                            :class="{ 'geo-student-highlight': isHighlighted(student) }"
                        >
                            <td class="geo-student-name">
                                {{ geoStudentName(student) }}
                            </td>
                            <td class="grey--text">
                                {{ geoStudentMeta(student) }}
                            </td>
                            <td class="grey--text">
                                {{ student.busAddress1 }}
                            </td>
                            <td>
                                <v-chip
                                    small
                                    label
                                    text-color="white"
                                    v-bind:color="
                                        student.isRegistered
                                            ? 'success'
                                            : '#e57373'
                                    "
                                    class="geo-enabled-chip"
                                >
                                    {{
                                        student.isRegistered
                                            ? "Enabled"
                                            : "Disabled"
                                    }}
                                </v-chip>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="title mb-0 mt-3" v-if="!geoDetails.length">
                No Student Stops
            </div>

            <div v-if="geoDetails.length">
                <div class="account-geo-section-title mt-2">
                    <v-divider></v-divider>
                    <div class="title mx-3 mb-0">Student Stops</div>
                    <v-divider></v-divider>
                </div>
                <div class="account-geo-updated caption text-info">
                    Last Updated: {{ lastUpdated }}
                </div>

                <div class="mt-1">
                    <div
                        v-for="student in geoDetails"
                        :key="student.studentID"
                        class="geo-student-box"
                        :class="{ 'geo-student-highlight': isHighlighted(student) }"
                    >
                        <div class="geo-student-header">
                            <div class="subheading font-weight-medium text-success mr-2">
                                {{ student.firstName }}
                            </div>
                            <div
                                class="caption grey--text mr-2"
                                v-if="studentMeta(student)"
                            >
                                {{ studentMeta(student) }}
                            </div>
                            <v-chip
                                small
                                label
                                text-color="white"
                                v-bind:color="
                                    student.isRegistered ? 'success' : '#e57373'
                                "
                                class="geo-enabled-chip"
                            >
                                {{ student.isRegistered ? "Enabled" : "Disabled" }}
                            </v-chip>
                        </div>

                        <table
                            class="geo-stops-table"
                            v-if="student.stops && student.stops.length"
                        >
                            <thead>
                                <tr>
                                    <th>Address</th>
                                    <th>Bus</th>
                                    <th>Stop</th>
                                    <th>Schedule(s)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(stop, sIndex) in student.stops" :key="sIndex">
                                    <td class="geo-stop-address">
                                        {{ stop.stopAddress }}
                                    </td>
                                    <td>
                                        <span
                                            class="geo-stop-badge"
                                            v-if="stop.bus"
                                            >{{ stop.bus }}</span
                                        >
                                    </td>
                                    <td>
                                        <span
                                            class="geo-stop-badge"
                                            v-if="hasStopNumber(stop)"
                                            >{{ stop.stopNumber }}</span
                                        >
                                    </td>
                                    <td>
                                        <div class="geo-schedule-chips">
                                            <span
                                                class="geo-schedule-chip"
                                                v-for="(
                                                    schedule, schIndex
                                                ) in stop.schedules"
                                                :key="schIndex"
                                                >{{ schedule }}</span
                                            >
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div class="caption grey--text" v-else>
                            No stops on file
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { groupBy } from "../../utils/helpers.js";
import moment from "moment";

export default {
    props: {
        account: Object,
        geoInfo: Object,
        highlightStudentID: String,
    },
    data() {
        return {
            geoDetails: [],
            geoStudents: [],
            phoneNumbers: [],
            lastUpdated: "",
            loading: true,
        };
    },
    async created() {
        this.load();
    },
    computed: {},
    methods: {
        async load() {
            try {
                const familyID =
                    this.account && this.account.accountID
                        ? this.account.accountID.trim()
                        : "";
                const encodedFamilyID = familyID.replace(/'/g, "''");

                const [response, geoRes] = await Promise.all([
                    this.$http.get(
                        "api/studentStop/getForFamily?familyID=" + familyID,
                    ),
                    this.$http
                        .get(
                            "odata/geoStudents?$filter=familyID eq '" +
                                encodedFamilyID +
                                "'",
                        )
                        .catch(() => null),
                ]);

                this.loading = false;

                const fromOdata =
                    geoRes && Array.isArray(geoRes.value) ? geoRes.value : [];
                const fromFamily =
                    response && response.geoStudents
                        ? response.geoStudents
                        : [];

                this.geoStudents = this.uniqueGeoStudents(
                    fromOdata.length ? fromOdata : fromFamily,
                );

                this.phoneNumbers =
                    (response && response.geoPhones) || [];

                if (
                    !response ||
                    !response.studentStops ||
                    !response.studentStops.length
                ) {
                    return;
                }

                this.lastUpdated = moment(
                    response.studentStops[0].updatedOn,
                ).format("MM/DD/YYYY h:mm:ss A");

                const group = groupBy(response.studentStops, "studentID");

                this.geoDetails = Object.keys(group).map((key) => {
                    const stopKey = key ? String(key).trim() : "";
                    const student = this.geoStudents.find((s) => {
                        const studentID = s.studentID
                            ? String(s.studentID).trim()
                            : "";
                        return studentID == stopKey;
                    });

                    return {
                        studentID: key,
                        firstName: group[key][0].firstName,
                        class: group[key][0].class,
                        branch: student && student.branch,
                        section: student && student.section,
                        building: student && student.building,
                        list: group[key],
                        stops: this.groupStops(group[key]),
                        isRegistered: student && student.isRegistered,
                    };
                });

                this.$forceUpdate();
            } catch (e) {
                this.loading = false;
            }
        },
        uniqueGeoStudents(list) {
            const seen = {};

            return (list || []).filter((student) => {
                const key = this.normalizeStudentId(student && student.studentID);

                if (!key || seen[key]) {
                    return false;
                }

                seen[key] = true;
                return true;
            });
        },
        normalizeStudentId(id) {
            return id == null || String(id).trim() === ""
                ? null
                : String(id).trim();
        },
        isHighlighted(student) {
            const key = this.normalizeStudentId(this.highlightStudentID);
            if (!key) {
                return false;
            }

            return this.normalizeStudentId(student && student.studentID) === key;
        },
        geoStudentKey(student) {
            return (
                this.normalizeStudentId(student && student.studentID) ||
                this.geoStudentName(student)
            );
        },
        geoStudentName(student) {
            const english = [student.engName, student.lastName]
                .filter((v) => v)
                .join(" ");

            if (english && student.hebName) {
                return english + " (" + student.hebName + ")";
            }

            return english || student.hebName || student.studentID || "";
        },
        geoStudentMeta(student) {
            const placement = [
                student.building,
                student.branch,
                student.class,
                student.section,
            ]
                .filter((v) => v)
                .join(" - ");

            return [this.normalizeStudentId(student.studentID), placement]
                .filter((v) => v)
                .join(" · ");
        },
        getPhoneType(phoneType) {
            switch (phoneType) {
                case "PHON":
                    return "Home Phone";
                case "CEL":
                    return "Cellphone";
                case "MCEL":
                    return "Mother Cellphone";
                default:
                    return phoneType || "Phone";
            }
        },
        // Many schedule rows (AM/PM, weekday/Friday/Sunday, regular/summer)
        // share the same address/bus/stop. Group those together so each
        // unique stop only takes one row, with all matching schedules listed.
        groupStops(list) {
            const groups = [];
            const groupsByKey = {};

            (list || []).forEach((geo) => {
                const key = [
                    geo.stopAddress || "",
                    geo.bus || "",
                    geo.stopNumber != null ? geo.stopNumber : "",
                ].join("|");

                if (!groupsByKey[key]) {
                    groupsByKey[key] = {
                        stopAddress: geo.stopAddress,
                        bus: geo.bus,
                        stopNumber: geo.stopNumber,
                        schedules: [],
                    };
                    groups.push(groupsByKey[key]);
                }

                if (geo.schedule) {
                    groupsByKey[key].schedules.push(geo.schedule);
                }
            });

            return groups;
        },
        studentMeta(student) {
            return [
                student.building,
                student.branch,
                student.class,
                student.section,
            ]
                .filter((v) => v)
                .join(" - ");
        },
        hasStopNumber(stop) {
            return stop.stopNumber !== null && stop.stopNumber !== undefined && stop.stopNumber !== "";
        },
    },
};
</script>
<style>
.account-geo-details {
    padding: 16px 16px 20px;
}
.account-geo-details .layout {
    margin-left: 0 !important;
    margin-right: 0 !important;
}
.account-geo-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 8px;
}
.account-geo-header-phones {
    text-align: left;
}
.account-geo-field {
    display: flex;
    align-items: baseline;
    margin-bottom: 6px;
}
.account-geo-section {
    margin-top: 4px;
}
.account-geo-section-title {
    display: flex;
    align-items: center;
}
.account-geo-updated {
    text-align: right;
    margin-top: 4px;
}
.geo-students-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin-top: 6px;
}
.geo-students-table th {
    text-align: left;
    font-weight: 500;
    color: #9e9e9e;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.3px;
    padding: 2px 12px 4px 8px;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
}
.geo-students-table td {
    padding: 4px 12px 4px 8px;
    vertical-align: middle;
    line-height: 1.3;
    white-space: nowrap;
}
.geo-students-table tbody tr:nth-child(even) {
    background-color: #e8e8e8;
}
.geo-student-name {
    font-weight: 500;
    color: #2e7d32;
}
.geo-student-highlight {
    background-color: #e8f5e9 !important;
}
.geo-students-table tbody tr.geo-student-highlight td:first-child {
    position: relative;
    padding-left: 14px;
}
.geo-students-table tbody tr.geo-student-highlight td:first-child::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 3px;
    bottom: 3px;
    width: 3px;
    border-radius: 2px;
    background: #4caf50;
}
.geo-student-box.geo-student-highlight {
    padding-left: 14px;
    position: relative;
}
.geo-student-box.geo-student-highlight::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 6px;
    bottom: 6px;
    width: 3px;
    border-radius: 2px;
    background: #4caf50;
}
.geo-student-box {
    border: 1px solid #c8e6c9;
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 8px;
}
.geo-student-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 4px;
}
.geo-enabled-chip {
    height: 20px;
    font-size: 11px;
    margin: 0;
}
.geo-stops-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin-bottom: 2px;
}
.geo-stops-table th {
    text-align: left;
    font-weight: 500;
    color: #9e9e9e;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.3px;
    padding: 0 10px 3px 0;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
}
.geo-stops-table td {
    padding: 4px 10px 4px 0;
    vertical-align: middle;
    line-height: 1.3;
}
.geo-stops-table tbody tr:nth-child(even) {
    background-color: #fafafa;
}
.geo-stop-address {
    font-weight: 500;
    color: #37474f;
    white-space: nowrap;
}
.geo-stop-badge {
    display: inline-block;
    background-color: #eceff1;
    color: #455a64;
    border-radius: 10px;
    padding: 1px 9px;
    font-size: 11px;
    white-space: nowrap;
}
.geo-schedule-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
.geo-schedule-chip {
    display: inline-block;
    background-color: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #a5d6a7;
    border-radius: 10px;
    padding: 1px 9px;
    font-size: 11px;
    line-height: 1.5;
    white-space: nowrap;
}
</style>
